import React, { useState } from "react";

const RuleCard = ({
  rule,
  onChange,
  factRegistry = [],
  actionRegistry = []
}) => {
  const [expanded, setExpanded] = useState(false);

  // Ensure safe defaults
  const conditions = rule.conditions_tree?.children || [];
  const actions = rule.actions || [];

  const toggleActive = () => {
    onChange({ ...rule, is_active: !rule.is_active });
  };

  const updateRuleName = (value) => {
    onChange({ ...rule, rule_name: value });
  };

  const updateConditions = (updatedConditions) => {
    onChange({
      ...rule,
      conditions_tree: {
        type: rule.conditions_tree?.type || "AND",
        children: updatedConditions
      }
    });
  };

  const updateActions = (updatedActions) => {
    onChange({
      ...rule,
      actions: updatedActions
    });
  };

  return (
    <div style={cardStyle}>
      {/* HEADER */}
      <div style={headerStyle}>
        <div onClick={() => setExpanded(!expanded)} style={{ flex: 1 }}>
          <div style={ruleTitle}>
            {rule.rule_name || "Unnamed Rule"}
          </div>

          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                ...statusBadge,
                background: rule.is_active ? "#28a745" : "#6c757d"
              }}
            >
              {rule.is_active ? "ACTIVE" : "INACTIVE"}
            </span>
            <span style={{ ...statusBadge, background: "#e9ecef", color: "#495057" }}>
              {rule.group_name || "DEFAULT"}
            </span>
            <span style={{ ...statusBadge, background: "#e9ecef", color: "#495057" }}>
              Priority {rule.priority ?? "—"}
            </span>
          </div>
        </div>

        {/* TOGGLE SWITCH */}
        <ToggleSwitch checked={rule.is_active} onChange={toggleActive} />

        <div style={{ marginLeft: 15, fontSize: 18, cursor: "pointer" }}
             onClick={() => setExpanded(!expanded)}>
          {expanded ? "▲" : "▼"}
        </div>
      </div>

      {/* BODY */}
      {expanded && (
        <div style={{ padding: 20 }}>

          {/* RULE NAME */}
          <SectionTitle title="Rule Configuration" />
          <input
            value={rule.rule_name || ""}
            onChange={(e) => updateRuleName(e.target.value)}
            style={inputStyle}
            placeholder="Enter rule name"
          />

          {/* CONDITION TYPE (AND / OR) */}
          <div style={{ marginBottom: 15 }}>
            <label style={{ fontSize: 13, marginRight: 10 }}>Condition Logic:</label>
            <select
              value={rule.conditions_tree?.type || "AND"}
              onChange={(e) =>
                onChange({
                  ...rule,
                  conditions_tree: {
                    ...rule.conditions_tree,
                    type: e.target.value
                  }
                })
              }
              style={smallInput}
            >
              <option value="AND">AND</option>
              <option value="OR">OR</option>
            </select>
          </div>

          {/* CONDITIONS */}
          <SectionTitle title="WHEN Conditions" />
          <ConditionBlock
            conditions={conditions}
            factRegistry={factRegistry}
            onChange={updateConditions}
          />

          {/* ACTIONS */}
          <SectionTitle title="THEN Actions" />
          <ActionBlock
            actions={actions}
            actionRegistry={actionRegistry}
            onChange={updateActions}
          />

          {/* SCHEDULING & PRIORITY */}
          <SectionTitle title="Scheduling & Priority" />
          <div style={{ fontSize: 11, color: "#888", marginBottom: 12 }}>
            Rules in the same <strong>Group</strong> compete — only the top
            <strong> Max Fires</strong> rules (lowest priority number) execute per cycle.
            Rules in different groups can fire simultaneously.
          </div>
          <div style={schedGrid}>
            <div>
              <label style={schedLabel}>Group Name</label>
              <input
                value={rule.group_name || ""}
                onChange={(e) => onChange({ ...rule, group_name: e.target.value })}
                style={smallInput}
                placeholder="DEFAULT"
              />
            </div>
            <div>
              <label style={schedLabel}>Priority <span style={{ color: "#888" }}>(lower = first)</span></label>
              <input
                type="number"
                min="1"
                value={rule.priority ?? ""}
                onChange={(e) => onChange({ ...rule, priority: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="100"
              />
            </div>
            <div>
              <label style={schedLabel}>Max Fires per Group</label>
              <input
                type="number"
                min="1"
                value={rule.group_max_fires ?? ""}
                onChange={(e) => onChange({ ...rule, group_max_fires: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="1"
              />
            </div>
            <div>
              <label style={schedLabel}>Cooldown (minutes)</label>
              <input
                type="number"
                min="0"
                value={rule.cooldown_minutes ?? ""}
                onChange={(e) => onChange({ ...rule, cooldown_minutes: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="0"
              />
            </div>
            <div>
              <label style={schedLabel}>Idempotency TTL (minutes)</label>
              <input
                type="number"
                min="0"
                value={rule.idempotency_ttl_minutes ?? ""}
                onChange={(e) => onChange({ ...rule, idempotency_ttl_minutes: parseInt(e.target.value) || "" })}
                style={smallInput}
                placeholder="60"
              />
            </div>
          </div>

        </div>
      )}
    </div>
  );
};

export default RuleCard;

/* ---------------- CONDITION BLOCK (recursive — handles leaf and group nodes) ---------------- */

const isGroup = (node) => node && (node.type === "AND" || node.type === "OR");

const ConditionBlock = ({ conditions, factRegistry, onChange, depth = 0 }) => {

  const addCondition = () => {
    onChange([
      ...conditions,
      {
        fact: "",
        operator: ">",
        value_type: "STATIC",
        value: "",
        value_from: "",
        expression: ""
      }
    ]);
  };

  const addGroup = () => {
    onChange([
      ...conditions,
      { type: "OR", children: [] }
    ]);
  };

  const removeAt = (index) => {
    const copy = [...conditions];
    copy.splice(index, 1);
    onChange(copy);
  };

  const updateLeaf = (index, field, value) => {
    const copy = conditions.map((c, i) => i === index ? { ...c, [field]: value } : c);
    if (field === "value_type") {
      copy[index] = { ...copy[index], value: "", value_from: "", expression: "" };
    }
    onChange(copy);
  };

  const updateGroupAt = (index, updatedNode) => {
    onChange(conditions.map((c, i) => i === index ? updatedNode : c));
  };

  const groupedFacts = factRegistry.reduce((acc, fact) => {
    if (!acc[fact.group_name]) acc[fact.group_name] = [];
    acc[fact.group_name].push(fact);
    return acc;
  }, {});

  const containerStyle = depth === 0 ? sectionBox : {
    ...sectionBox,
    background: "#eef2ff",
    border: "1px dashed #7b8cde",
    marginBottom: 0,
  };

  return (
    <div style={containerStyle}>
      {conditions.length === 0 && (
        <div style={emptyStyle}>No conditions added</div>
      )}

      {conditions.map((c, i) => {

        if (isGroup(c)) {
          return (
            <div key={i} style={{ marginBottom: 12 }}>
              {/* Group header: type toggle + remove */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "#555" }}>Group:</span>
                <select
                  value={c.type}
                  onChange={(e) => updateGroupAt(i, { ...c, type: e.target.value })}
                  style={{ ...smallInput, width: 80 }}
                >
                  <option value="AND">AND</option>
                  <option value="OR">OR</option>
                </select>
                <button onClick={() => removeAt(i)} style={{ ...dangerBtn, fontSize: 11, padding: "3px 8px" }}>
                  Remove Group
                </button>
              </div>
              {/* Recursive nested block */}
              <ConditionBlock
                conditions={c.children || []}
                factRegistry={factRegistry}
                onChange={(newChildren) => updateGroupAt(i, { ...c, children: newChildren })}
                depth={depth + 1}
              />
            </div>
          );
        }

        /* Leaf condition */
        return (
          <div key={i} style={{ marginBottom: 15 }}>
            <div style={rowStyleEditable}>
              <select
                value={c.fact}
                onChange={(e) => updateLeaf(i, "fact", e.target.value)}
                style={smallInput}
              >
                <option value="">Select Fact</option>
                {Object.entries(groupedFacts).map(([group, facts]) => (
                  <optgroup key={group} label={group}>
                    {facts.map(f => (
                      <option key={f.fact_key} value={f.fact_key}>{f.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>

              <select
                value={c.operator}
                onChange={(e) => updateLeaf(i, "operator", e.target.value)}
                style={smallInput}
              >
                {["<", "<=", "==", ">=", ">", "!="].map(op => (
                  <option key={op} value={op}>{op}</option>
                ))}
              </select>

              <select
                value={c.value_type || "STATIC"}
                onChange={(e) => updateLeaf(i, "value_type", e.target.value)}
                style={smallInput}
              >
                <option value="STATIC">Static</option>
                <option value="DYNAMIC">Dynamic</option>
                <option value="EXPRESSION">Expression</option>
              </select>

              <button onClick={() => removeAt(i)} style={dangerBtn}>✕</button>
            </div>

            {c.value_type === "STATIC" && (
              <input
                value={c.value ?? ""}
                onChange={(e) => updateLeaf(i, "value", e.target.value)}
                style={inputStyle}
                placeholder="Enter static value"
              />
            )}

            {c.value_type === "DYNAMIC" && (
              <select
                value={c.value_from || ""}
                onChange={(e) => updateLeaf(i, "value_from", e.target.value)}
                style={inputStyle}
              >
                <option value="">Select Fact</option>
                {Object.entries(groupedFacts).map(([group, facts]) => (
                  <optgroup key={group} label={group}>
                    {facts.map(f => (
                      <option key={f.fact_key} value={f.fact_key}>{f.label}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
            )}

            {c.value_type === "EXPRESSION" && (
              <input
                value={c.expression || ""}
                onChange={(e) => updateLeaf(i, "expression", e.target.value)}
                style={inputStyle}
                placeholder="Example: strategy.next_buy_price * 1.02"
              />
            )}
          </div>
        );
      })}

      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={addCondition} style={secondaryBtn}>+ Add Condition</button>
        {depth === 0 && (
          <button onClick={addGroup} style={{ ...secondaryBtn, borderColor: "#7b8cde", color: "#5060c0" }}>
            + Add OR / AND Group
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------------- ACTION BLOCK ---------------- */

// Actions that accept a min_net_profit parameter configurable from the UI
const MONEY_MGMT_PARAM_ACTIONS = new Set([
  "SQUARE_OFF_MAX_DECAY_POSITION",
  "SQUARE_OFF_MIN_STOCK_MAX_DECAY_POSITION",
  "SQUARE_OFF_FUTURES_MAX_CALL_DECAY_POSITION",
]);

const ActionBlock = ({ actions, actionRegistry, onChange }) => {

  const addAction = () => {
    onChange([
      ...actions,
      { action_key: "", params: {} }
    ]);
  };

  const removeAction = (index) => {
    const copy = [...actions];
    copy.splice(index, 1);
    onChange(copy);
  };

  const updateAction = (index, value) => {
    const copy = [...actions];
    copy[index].action_key = value;
    onChange(copy);
  };

  const updateActionParam = (index, paramKey, value) => {
    const copy = [...actions];
    copy[index] = { ...copy[index], [paramKey]: value };
    onChange(copy);
  };

  const groupedActions = actionRegistry.reduce((acc, act) => {
    if (!acc[act.group_name]) acc[act.group_name] = [];
    acc[act.group_name].push(act);
    return acc;
  }, {});

  return (
    <div style={sectionBox}>
      {actions.length === 0 && (
        <div style={emptyStyle}>No actions added</div>
      )}

      {actions.map((a, i) => {

        const meta = actionRegistry.find(
          x => x.action_key === a.action_key
        );

        return (
          <div key={i} style={{ marginBottom: 15 }}>

            <div style={rowStyleEditable}>

              <select
                value={a.action_key}
                onChange={(e) => updateAction(i, e.target.value)}
                style={smallInput}
              >
                <option value="">Select Action</option>
                {Object.entries(groupedActions).map(([group, acts]) => (
                  <optgroup key={group} label={group}>
                    {acts.map(act => (
                      <option key={act.action_key} value={act.action_key}>
                        {act.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>

              <div></div>
              <div></div>

              <button onClick={() => removeAction(i)} style={dangerBtn}>
                ✕
              </button>
            </div>

            {meta && (
              <div style={{
                marginTop: 6,
                fontSize: 12,
                color: "#555",
                background: "#f1f3f5",
                padding: 8,
                borderRadius: 6
              }}>
                <strong>{meta.label}</strong><br/>
                {meta.tooltip || meta.description}
              </div>
            )}

            {MONEY_MGMT_PARAM_ACTIONS.has(a.action_key) && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
                <label style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
                  Min Net Profit (Rs.):
                </label>
                <input
                  type="number"
                  min="0"
                  step="1000"
                  value={a.min_net_profit ?? 5000}
                  onChange={(e) =>
                    updateActionParam(i, "min_net_profit", parseFloat(e.target.value) || 0)
                  }
                  style={{ ...smallInput, width: 120 }}
                  placeholder="5000"
                />
              </div>
            )}

            {a.action_key === "TRIM_CALL_LOT_ROLLDOWN" && (
              <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <label style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
                  Min Calls : Hedged Lots (strictly above):
                </label>
                <input
                  type="number"
                  min="1"
                  step="0.5"
                  value={a.min_ratio ?? 3}
                  onChange={(e) =>
                    updateActionParam(i, "min_ratio", parseFloat(e.target.value) || 0)
                  }
                  style={{ ...smallInput, width: 80 }}
                  placeholder="3"
                />
                <label style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>
                  Min Margin Release (Rs.):
                </label>
                <input
                  type="number"
                  min="0"
                  step="5000"
                  value={a.min_margin_release ?? 50000}
                  onChange={(e) =>
                    updateActionParam(i, "min_margin_release", parseFloat(e.target.value) || 0)
                  }
                  style={{ ...smallInput, width: 120 }}
                  placeholder="50000"
                />
              </div>
            )}

          </div>
        );
      })}

      <button onClick={addAction} style={secondaryBtn}>
        + Add Action
      </button>
    </div>
  );
};

const SectionTitle = ({ title }) => (
  <h4 style={sectionTitleStyle}>{title}</h4>
);

/* ---------------- STYLES ---------------- */

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  marginBottom: 20,
  boxShadow: "0 4px 12px rgba(0,0,0,0.05)"
};

const headerStyle = {
  padding: 16,
  display: "flex",
  alignItems: "center",
  borderBottom: "1px solid #eee"
};

const ruleTitle = {
  fontSize: 16,
  fontWeight: 600
};

const statusBadge = {
  fontSize: 11,
  color: "#fff",
  padding: "3px 8px",
  borderRadius: 12
};

const sectionTitleStyle = {
  marginTop: 25,
  marginBottom: 15,
  fontSize: 14,
  fontWeight: 600
};

const sectionBox = {
  background: "#f8f9fb",
  padding: 15,
  borderRadius: 8,
  marginBottom: 15
};

const inputStyle = {
  padding: "8px 12px",
  borderRadius: 8,
  border: "1px solid #ccc",
  fontSize: 13,
  width: "100%",
  marginBottom: 15
};

const rowStyleEditable = {
  display: "grid",
  gridTemplateColumns: "2fr 1fr 1fr 40px",
  gap: 10,
  marginBottom: 10
};

const smallInput = {
  padding: "6px 8px",
  borderRadius: 6,
  border: "1px solid #ccc",
  fontSize: 13,
  width: "100%"
};

const secondaryBtn = {
  padding: "6px 12px",
  borderRadius: 8,
  border: "1px solid #ccc",
  background: "#fff",
  cursor: "pointer"
};

const dangerBtn = {
  background: "#dc3545",
  color: "#fff",
  border: "none",
  borderRadius: 6,
  cursor: "pointer"
};

const emptyStyle = {
  fontSize: 12,
  color: "#888",
  marginBottom: 10
};

const schedGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(5, 1fr)",
  gap: 12,
  marginBottom: 15
};

const schedLabel = {
  display: "block",
  fontSize: 11,
  color: "#555",
  marginBottom: 4,
  fontWeight: 500
};

/* TOGGLE SWITCH */

const ToggleSwitch = ({ checked, onChange }) => (
  <div
    onClick={onChange}
    title={checked ? "Active — click to deactivate" : "Inactive — click to activate"}
    style={{
      width: 52,
      height: 28,
      borderRadius: 14,
      backgroundColor: checked ? "#28a745" : "#ccc",
      position: "relative",
      cursor: "pointer",
      transition: "background-color 0.25s",
      flexShrink: 0,
      boxShadow: "inset 0 1px 3px rgba(0,0,0,0.15)",
    }}
  >
    <div
      style={{
        position: "absolute",
        top: 4,
        left: checked ? 28 : 4,
        width: 20,
        height: 20,
        borderRadius: "50%",
        backgroundColor: "#fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
        transition: "left 0.25s",
      }}
    />
  </div>
);